import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, Checkbox, SpinLoading, Form, Input, Toast } from 'antd-mobile'
import { validateVerificationCode } from '../utils/validation'

/**
 * 欢迎/提示页面 - 统一入口
 *
 * URL: https://h.blet.cc/:braceletId
 *
 * 功能：
 * 1. 检查手环状态（已激活/未激活）
 * 2. 未激活：显示协议 → 跳转激活页面
 * 3. 已激活：显示协议 → 验证码验证 → 显示详情
 */

type PageStep = 'welcome' | 'verify' | 'detail'

interface UserInfo {
  name: string
  age: number
  gender: string
  blood_type: string
  phone: string
  emergency_contact_name: string
  emergency_contact_phone: string
  emergency_contact_relation: string
  address?: string
  chronic_diseases?: string
  allergies?: string
  medications?: string
  notes?: string
}

interface BraceletInfo {
  bracelet_id: string
  status: string
  activated_at?: string
  user_info?: UserInfo
}

export default function WelcomePage() {
  const { braceletId } = useParams<{ braceletId: string }>()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [isActivated, setIsActivated] = useState(false)
  const [currentStep, setCurrentStep] = useState<PageStep>('welcome')
  const [agreedTOS, setAgreedTOS] = useState(false)
  const [agreedPrivacy, setAgreedPrivacy] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [braceletInfo, setBraceletInfo] = useState<BraceletInfo | null>(null)
  const [error, setError] = useState('')
  const [codeError, setCodeError] = useState('')

  useEffect(() => {
    checkBraceletStatus()
  }, [braceletId])

  // 检查手环状态
  const checkBraceletStatus = async () => {
    setLoading(true)
    try {
      const response = await fetch(`/api/bracelets/${braceletId}/info`)

      if (response.ok) {
        const data = await response.json()
        setIsActivated(data.status === 'active')
      } else if (response.status === 404) {
        // 手环不存在，视为未激活
        setIsActivated(false)
      } else {
        setError('无法获取手环信息，请稍后重试')
      }
    } catch (err) {
      console.error('Check status error:', err)
      // 网络错误时假设未激活，允许用户继续
      setIsActivated(false)
    } finally {
      setLoading(false)
    }
  }

  // 继续按钮点击
  const handleContinue = () => {
    if (!agreedTOS || !agreedPrivacy) {
      return // 按钮应该是禁用状态，这里做二次检查
    }

    if (isActivated) {
      // 已激活 → 进入验证码验证步骤
      setCurrentStep('verify')
    } else {
      // 未激活 → 跳转到激活页面
      navigate(`/activate/${braceletId}`)
    }
  }

  // 验证码实时验证
  const validateCode = (code: string) => {
    if (!code) {
      setCodeError('请输入验证码')
      return false
    }
    if (!validateVerificationCode(code.toUpperCase())) {
      setCodeError('验证码必须是6位大写字母或数字')
      return false
    }
    setCodeError('')
    return true
  }

  // 验证码验证
  const handleVerify = async () => {
    if (!validateCode(verificationCode)) {
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/api/bracelets/${braceletId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_code: verificationCode }),
      })

      const data = await response.json()

      if (response.ok) {
        Toast.show({ icon: 'success', content: '验证成功！' })
        // 验证成功后获取手环详细信息
        await fetchBraceletDetail()
      } else {
        Toast.show({
          icon: 'fail',
          content: data.detail || data.message || '验证码错误',
        })
      }
    } catch (error) {
      console.error('Verification error:', error)
      Toast.show({ icon: 'fail', content: '网络错误，请重试' })
    } finally {
      setLoading(false)
    }
  }

  // 获取手环详细信息
  const fetchBraceletDetail = async () => {
    setLoading(true)
    try {
      const response = await fetch(`/api/bracelets/${braceletId}`)
      const data = await response.json()

      if (response.ok) {
        setBraceletInfo(data)
        setCurrentStep('detail')
      } else {
        setError(data.detail || data.message || '查询失败')
      }
    } catch (err) {
      console.error('Query error:', err)
      setError('网络错误，请重试')
    } finally {
      setLoading(false)
    }
  }

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`
  }

  const handleSMS = (phone: string) => {
    window.location.href = `sms:${phone}`
  }

  // 加载中
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <SpinLoading style={{ fontSize: 48, color: '#1677ff' }} />
          <p className="mt-4 text-lg text-gray-600">
            {currentStep === 'welcome' && '正在检查手环状态...'}
            {currentStep === 'verify' && '正在验证...'}
            {currentStep === 'detail' && '正在加载信息...'}
          </p>
        </div>
      </div>
    )
  }

  // 错误状态
  if (error) {
    return (
      <div className="min-h-screen bg-white p-4 flex items-center justify-center">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">系统错误</h3>
          <p className="text-lg text-gray-600 mb-8">{error}</p>
          <Button
            block
            color="primary"
            onClick={() => window.location.reload()}
            style={{
              height: '52px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '12px'
            }}
          >
            重新加载
          </Button>
        </div>
      </div>
    )
  }

  // 验证码验证步骤 - 紧急场景优化版
  if (currentStep === 'verify') {
    return (
      <div className="min-h-screen bg-white p-4">
        <div className="max-w-2xl mx-auto pt-12">
          {/* 紧急情况标识 */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600 rounded-full mb-3">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              紧急查询
            </h1>
            <p className="text-xl text-red-600 font-semibold">
              查看紧急联系人和医疗信息
            </p>
          </div>

          <div className="bg-white border-2 border-red-200 rounded-xl p-8">
            {/* 操作指引 - 步骤1 */}
            <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-2xl font-bold">1</span>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-bold text-orange-800 mb-2">
                    请在手环内侧查找验证码
                  </h3>
                  <p className="text-lg text-orange-700 leading-relaxed">
                    <strong>翻看手环内侧</strong>，找到 <strong>6 位字母数字组合</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* 操作指引 - 步骤2 */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-2xl font-bold">2</span>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    输入验证码
                  </h3>
                  <p className="text-lg text-gray-700">
                    在下方输入框中填写6位验证码
                  </p>
                </div>
              </div>
            </div>

            {/* 验证码输入 */}
            <Form layout="vertical">
              <Form.Item>
                <div className="mb-3">
                  <label className="text-xl font-bold text-gray-900">验证码</label>
                </div>
                <Input
                  placeholder="6位验证码"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(val) => {
                    setVerificationCode(val.toUpperCase())
                    validateCode(val)
                  }}
                  onBlur={() => validateCode(verificationCode)}
                  style={{
                    '--font-size': '24px',
                    '--text-align': 'center',
                    fontSize: '24px',
                    height: '64px',
                    letterSpacing: '0.4em',
                    borderRadius: '12px',
                    border: codeError ? '2px solid #ff4d4f' : '2px solid #cbd5e1'
                  }}
                />
                {codeError && (
                  <div className="text-red-500 text-sm mt-2">{codeError}</div>
                )}
              </Form.Item>

              <Button
                block
                color="danger"
                size="large"
                loading={loading}
                onClick={handleVerify}
                style={{
                  height: '64px',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  marginTop: '24px',
                  borderRadius: '12px'
                }}
              >
                确认查看（即同意协议）
              </Button>
            </Form>

            {/* 协议说明 - 简化版 */}
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600 text-center leading-relaxed">
                点击"确认查看"即表示您已阅读并同意
                <a href="/privacypolicy-zh.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline mx-1">
                  第三方隐私协议
                </a>
                和
                <a href="/tos-zh.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline mx-1">
                  服务条款
                </a>
              </p>
            </div>

            {/* 验证后可查看的信息 */}
            <div className="mt-6 p-5 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-bold text-green-900 mb-3 text-lg flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                验证后可查看
              </h3>
              <ul className="space-y-2 text-base text-green-800">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>紧急联系人姓名、电话、关系</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>佩戴者基本信息（姓名、年龄、血型）</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>重要医疗信息（过敏史、用药、疾病）</span>
                </li>
              </ul>
            </div>

            <div className="text-center mt-6">
              <Button
                color="default"
                onClick={() => setCurrentStep('welcome')}
                style={{
                  height: '48px',
                  fontSize: '17px',
                  borderRadius: '10px',
                  paddingLeft: '32px',
                  paddingRight: '32px'
                }}
              >
                返回上一步
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 详情展示步骤
  if (currentStep === 'detail' && braceletInfo) {
    // 检查手环状态
    if (braceletInfo.status !== 'active') {
      return (
        <div className="min-h-screen bg-white p-4">
          <div className="max-w-2xl mx-auto pt-20">
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-6">
                <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">手环未激活</h3>
              <p className="text-lg text-gray-600 mb-8">
                此手环尚未激活，请先完成激活流程。
              </p>
              <Button
                block
                color="primary"
                onClick={() => navigate(`/activate/${braceletId}`)}
                style={{
                  height: '52px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  borderRadius: '12px'
                }}
              >
                前往激活
              </Button>
            </div>
          </div>
        </div>
      )
    }

    // 检查用户信息
    if (!braceletInfo.user_info) {
      return (
        <div className="min-h-screen bg-white p-4">
          <div className="max-w-2xl mx-auto pt-20">
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
                <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">暂无信息</h3>
              <p className="text-lg text-gray-600 mb-8">
                手环已激活，但暂无用户信息。
              </p>
              <Button
                block
                color="primary"
                onClick={() => { window.location.href = '/' }}
                style={{
                  height: '52px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  borderRadius: '12px'
                }}
              >
                返回首页
              </Button>
            </div>
          </div>
        </div>
      )
    }

    const userInfo = braceletInfo.user_info

    return (
      <div className="min-h-screen bg-white p-4 pb-20">
        <div className="max-w-2xl mx-auto pt-6">
          {/* 顶部Logo和标题 */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-3">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              紧急联系信息
            </h1>
            <p className="text-lg text-gray-600">
              如需帮助，请联系下方联系人
            </p>
            {/* 手环ID - 弱化显示 */}
            <div className="mt-3">
              <span className="text-xs text-gray-400">设备编号：{braceletInfo.bracelet_id}</span>
            </div>
          </div>

          {/* 佩戴者基本信息 */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">佩戴者信息</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-base text-gray-600">姓名</span>
                <span className="text-lg font-semibold text-gray-900">{userInfo.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-base text-gray-600">年龄</span>
                <span className="text-lg font-semibold text-gray-900">{userInfo.age} 岁</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-base text-gray-600">性别</span>
                <span className="text-lg font-semibold text-gray-900">{userInfo.gender}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-base text-gray-600">血型</span>
                <span className="text-lg font-semibold text-gray-900">{userInfo.blood_type}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-base text-gray-600">联系电话</span>
                <span className="text-lg font-semibold text-gray-900">{userInfo.phone}</span>
              </div>
              {userInfo.address && (
                <div className="pt-2 border-t border-gray-200">
                  <div className="text-base text-gray-600 mb-1">居住地址</div>
                  <div className="text-base font-medium text-gray-900">{userInfo.address}</div>
                </div>
              )}
            </div>
          </div>

          {/* 紧急联系人 - 突出显示 */}
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-4">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-3">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-red-700">
                紧急联系人
              </h3>
            </div>

            <div className="space-y-3 mb-5">
              <div className="flex justify-between items-center">
                <span className="text-lg text-gray-700">姓名</span>
                <span className="text-xl font-bold text-gray-900">{userInfo.emergency_contact_name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg text-gray-700">关系</span>
                <span className="text-xl font-bold text-gray-900">{userInfo.emergency_contact_relation}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg text-gray-700">电话</span>
                <span className="text-xl font-bold text-gray-900">{userInfo.emergency_contact_phone}</span>
              </div>
            </div>

            {/* 快捷操作按钮 */}
            <div className="flex gap-3">
              <Button
                block
                color="danger"
                size="large"
                onClick={() => handleCall(userInfo.emergency_contact_phone)}
                style={{
                  flex: 1,
                  height: '56px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  borderRadius: '12px'
                }}
              >
                📞 拨打电话
              </Button>
              <Button
                block
                color="primary"
                size="large"
                onClick={() => handleSMS(userInfo.emergency_contact_phone)}
                style={{
                  flex: 1,
                  height: '56px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  borderRadius: '12px'
                }}
              >
                💬 发送短信
              </Button>
            </div>
          </div>

          {/* 医疗信息 */}
          {(userInfo.chronic_diseases || userInfo.allergies || userInfo.medications) && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-4">
              <div className="flex items-center mb-4">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-yellow-500 rounded-full mr-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-yellow-900">重要医疗信息</h3>
              </div>

              <div className="space-y-4">
                {userInfo.chronic_diseases && (
                  <div>
                    <div className="text-base font-semibold text-gray-700 mb-2">慢性疾病 / 既往病史</div>
                    <div className="bg-white border border-yellow-200 rounded-lg p-3">
                      <p className="text-base text-red-700 leading-relaxed">{userInfo.chronic_diseases}</p>
                    </div>
                  </div>
                )}
                {userInfo.allergies && (
                  <div>
                    <div className="text-base font-semibold text-gray-700 mb-2">药物过敏史</div>
                    <div className="bg-white border border-yellow-200 rounded-lg p-3">
                      <p className="text-base text-red-700 leading-relaxed">{userInfo.allergies}</p>
                    </div>
                  </div>
                )}
                {userInfo.medications && (
                  <div>
                    <div className="text-base font-semibold text-gray-700 mb-2">正在服用的药物</div>
                    <div className="bg-white border border-yellow-200 rounded-lg p-3">
                      <p className="text-base text-blue-700 leading-relaxed">{userInfo.medications}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 温馨提示 */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-4">
            <div className="flex items-center mb-3">
              <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full mr-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-blue-900">温馨提示</h4>
            </div>
            <ul className="space-y-2 text-base text-blue-800">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">•</span>
                <span className="leading-relaxed">如发现佩戴者需要帮助，请立即联系紧急联系人</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">•</span>
                <span className="leading-relaxed">如遇紧急医疗情况，请先拨打 120 急救电话</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">•</span>
                <span className="leading-relaxed">请注意佩戴者的过敏史和用药情况，告知医护人员</span>
              </li>
            </ul>
          </div>

          {/* 底部操作 */}
          <div className="text-center">
            <Button
              color="default"
              onClick={() => { window.location.href = '/' }}
              style={{
                height: '48px',
                fontSize: '17px',
                borderRadius: '10px',
                paddingLeft: '32px',
                paddingRight: '32px'
              }}
            >
              返回首页
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // 欢迎步骤（默认）
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* 顶部标题区域 */}
        <div className="text-center mb-10">
          {/* Logo/品牌区域 */}
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-4">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            守护手环服务
          </h1>
          <p className="text-xl text-gray-600 mb-1">
            {isActivated ? '查看紧急联系信息' : '激活您的守护手环'}
          </p>

          {/* 手环ID - 弱化显示 */}
          <div className="mt-4">
            <span className="text-xs text-gray-400">设备编号：{braceletId}</span>
          </div>
        </div>

        {/* 状态标识 */}
        <div className="text-center mb-8">
          {isActivated ? (
            <div className="inline-flex items-center px-6 py-3 bg-green-50 border border-green-200 rounded-lg">
              <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-lg font-semibold text-green-700">设备已激活</span>
            </div>
          ) : (
            <div className="inline-flex items-center px-6 py-3 bg-blue-50 border border-blue-200 rounded-lg">
              <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-lg font-semibold text-blue-700">待激活设备</span>
            </div>
          )}
        </div>

        {/* 提示内容 */}
        <div className="bg-white border border-gray-200 rounded-xl p-8 mb-6">
          {isActivated ? (
            // 已激活提示
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                查看紧急信息
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <p className="ml-4 leading-relaxed">
                    您可以查看佩戴者的联系信息
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <p className="ml-4 leading-relaxed">
                    紧急情况请直接拨打联系人电话
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <p className="ml-4 leading-relaxed">
                    危急时刻请先拨打 120 急救
                  </p>
                </div>
              </div>

              <div className="mt-8 p-5 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-bold text-blue-900 mb-2 text-lg">隐私保护提醒</h3>
                <p className="text-base text-blue-800 leading-relaxed">
                  请仅在紧急情况下使用这些信息，不得用于其他用途。
                </p>
              </div>
            </div>
          ) : (
            // 未激活提示
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                激活流程
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <p className="ml-4 leading-relaxed">
                    输入手环背面验证码
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <p className="ml-4 leading-relaxed">
                    填写基本信息
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <p className="ml-4 leading-relaxed">
                    完成激活开始守护
                  </p>
                </div>
              </div>

              <div className="mt-8 p-5 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="font-bold text-amber-900 mb-2 text-lg">重要提醒</h3>
                <p className="text-base text-amber-800 leading-relaxed">
                  请确保填写的信息准确，以便紧急情况下快速联系。
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 法律协议 */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h3 className="font-bold text-gray-900 text-xl mb-5">使用协议</h3>

          {/* 服务条款 */}
          <div className="mb-4">
            <Checkbox
              checked={agreedTOS}
              onChange={(checked) => setAgreedTOS(checked)}
              style={{ '--icon-size': '22px', '--font-size': '17px' }}
            >
              <span className="text-base text-gray-700">
                我已阅读并同意
                <a href="/tos-zh.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium underline mx-1">
                  服务条款
                </a>
              </span>
            </Checkbox>
          </div>

          {/* 隐私协议 */}
          <div className="mb-5">
            <Checkbox
              checked={agreedPrivacy}
              onChange={(checked) => setAgreedPrivacy(checked)}
              style={{ '--icon-size': '22px', '--font-size': '17px' }}
            >
              <span className="text-base text-gray-700">
                我已阅读并同意
                <a
                  href={isActivated ? "/privacy-viewer" : "/privacypolicy-zh.html"}
                  target="_blank"
                  className="text-blue-600 font-medium underline mx-1"
                >
                  {isActivated ? '第三方隐私协议' : '隐私保护协议'}
                </a>
              </span>
            </Checkbox>
          </div>

          {/* 风险提示 */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 leading-relaxed">
            <strong className="text-gray-900">重要提示：</strong>
            本服务基于二维码技术,依赖他人善意。我们会保护信息安全，但无法保证绝对安全。
          </div>
        </div>

        {/* 继续按钮 */}
        <Button
          block
          color="primary"
          size="large"
          disabled={!agreedTOS || !agreedPrivacy}
          onClick={handleContinue}
          style={{
            height: '56px',
            fontSize: '20px',
            fontWeight: 'bold',
            borderRadius: '12px'
          }}
        >
          {isActivated ? '查看联系信息' : '开始激活'}
        </Button>

        {/* 底部链接 */}
        <div className="text-center mt-6 text-base text-gray-500">
          <a href="/" className="hover:text-blue-600 transition-colors">返回首页</a>
        </div>
      </div>
    </div>
  )
}
